import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Champ, styleChamp } from './champs';
import { heureDe, jourDe } from '../format';
import type { ModificationEvenement } from '@colette/shared';
import type { Evenement } from '../../api/types';

/**
 * Modifier un evenement DEJA dans le planning (lot E1).
 *
 * Le formulaire part de l'evenement affiche et n'envoie QUE ce qui a change : un PATCH qui
 * renverrait tout ecraserait des champs que l'ecran ne montre pas. Les valeurs de depart
 * sont calculees dans le fuseau du FOYER — l'evenement porte un instant, pas une heure.
 */
export function FormulaireEvenement({
  evenement,
  fuseau,
  estUneSerie,
  occupe,
  onEnregistrer,
  onAnnuler,
}: {
  // Seuls les champs REELLEMENT lus sont exiges : « Plus tard » n'a pas l'objet
  // entier, et en fabriquer un faux pour satisfaire un type serait un mensonge.
  evenement: Pick<Evenement, 'title' | 'startsAt' | 'endsAt' | 'allDay' | 'location' | 'transport'>;
  fuseau: string;
  estUneSerie: boolean;
  occupe: boolean;
  onEnregistrer: (modification: ModificationEvenement) => void;
  onAnnuler: () => void;
}) {
  const jourInitial = jourDe(evenement.startsAt, fuseau);
  const heureInitiale = evenement.allDay ? '' : heureDe(evenement.startsAt, fuseau);
  const finInitiale =
    evenement.endsAt === null || evenement.allDay ? '' : heureDe(evenement.endsAt, fuseau);
  const lieuInitial = evenement.location ?? '';
  const transportInitial = evenement.transport ?? '';

  const [titre, setTitre] = useState(evenement.title);
  const [jour, setJour] = useState(jourInitial);
  const [heure, setHeure] = useState(heureInitiale);
  const [fin, setFin] = useState(finInitiale);
  const [lieu, setLieu] = useState(lieuInitial);
  const [transport, setTransport] = useState(transportInitial);

  const enregistrer = () => {
    if (occupe) return;
    const modification: ModificationEvenement = {};
    if (titre.trim() !== evenement.title) modification.title = titre.trim();
    if (jour !== jourInitial) modification.date = jour;
    if (heure !== heureInitiale) modification.time = heure === '' ? null : heure;
    if (fin !== finInitiale) modification.endTime = fin === '' ? null : fin;
    if (lieu.trim() !== lieuInitial) modification.location = lieu.trim() === '' ? null : lieu.trim();
    if (transport.trim() !== transportInitial) {
      modification.transport = transport.trim() === '' ? null : transport.trim();
    }
    // Rien n'a bouge : on referme sans deranger le serveur (qui refuserait un corps vide).
    if (Object.keys(modification).length === 0) {
      onAnnuler();
      return;
    }
    onEnregistrer(modification);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        enregistrer();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {estUneSerie ? (
        <div style={{ fontSize: 12, color: C.corailTexte, fontWeight: 700 }}>
          Cette modification s’applique à toute la série.
        </div>
      ) : null}

      <Champ libelle="Titre">
        <input
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          aria-label="Titre de l’événement"
          style={styleChamp}
        />
      </Champ>

      {/* Le JOUR occupe sa propre ligne. Avec les deux heures a cote de lui, il restait
          267 px pour trois champs sur un telephone de 390 px — le formulaire s'ouvre dans
          trois emboitements (le lisere de la journee, la carte de l'evenement, la sienne).
          Or Chrome Android refuse de reduire un champ date sous ~130 px et un champ heure
          sous ~95 px : il en faut ~340. Le formulaire DEBORDAIT de l'ecran, « Annuler »
          compris (signale par le porteur le 2026-08-28). */}
      <Champ libelle="Jour">
        <input
          type="date"
          value={jour}
          onChange={(e) => setJour(e.target.value)}
          aria-label="Jour de l’événement"
          style={styleChamp}
        />
      </Champ>

      {/* Les deux heures tiennent, elles, cote a cote : les empiler ferait un formulaire
          a rallonge, et elles se lisent ensemble (« de … a … »). */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Champ libelle="Heure">
            <input
              type="time"
              value={heure}
              onChange={(e) => setHeure(e.target.value)}
              aria-label="Heure de l’événement"
              style={styleChamp}
            />
          </Champ>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Champ libelle="Fin">
            <input
              type="time"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              aria-label="Heure de fin"
              style={styleChamp}
            />
          </Champ>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.tertiaire, marginTop: -6 }}>
        Sans heure, l’événement dure toute la journée.
      </div>

      <Champ libelle="Lieu">
        <input
          value={lieu}
          onChange={(e) => setLieu(e.target.value)}
          aria-label="Lieu de l’événement"
          placeholder="Facultatif"
          style={styleChamp}
        />
      </Champ>

      {/* Sur sa PROPRE ligne : deux champs par rangee au maximum dans un formulaire
          imbrique — la lecon du 2026-08-29, payee deux fois. */}
      <Champ libelle="Comment j’y vais">
        <input
          value={transport}
          onChange={(e) => setTransport(e.target.value)}
          aria-label="Comment j’y vais"
          placeholder="RER B, TER, voiture…"
          style={styleChamp}
        />
      </Champ>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={occupe}
          className="pressable"
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 12,
            background: occupe ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 14.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: occupe ? 'default' : 'pointer',
          }}
        >
          {occupe ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          className="pressable"
          aria-label="Annuler la modification"
          onClick={onAnnuler}
          style={{
            height: 44,
            padding: '0 16px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 12,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
